import React, { useState, useEffect } from 'react';
import { useAllTagsStore, notifyTagsChanged } from '../../stores/tagStore';
import { createTag } from '../../services/api';
import { useTranslation } from 'react-i18next';
import { PlusIcon, MagnifyingGlassIcon, CheckIcon } from '@heroicons/react/24/outline';
import { CustomSelect } from '@redpanda/ui';

interface TagCloudSelectorProps {
  value?: string[];
  onChange: (value: string[]) => void;
}

// 快速新增標籤時依分類給的預設顏色（之後可在標籤管理修改）
const CATEGORY_DEFAULT_COLORS: Record<string, string> = {
  INDUSTRY: '#3B82F6',
  SIZE: '#10B981',
  STATUS: '#F59E0B',
  CUSTOM: '#8B5CF6',
};

const TagCloudSelector: React.FC<TagCloudSelectorProps> = ({ value = [], onChange }) => {
  const { t } = useTranslation();
  // 全部標籤（不是標籤管理頁的分頁資料）
  const tags = useAllTagsStore(state => state.allTags);
  const fetchAllTags = useAllTagsStore(state => state.fetchAllTags);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [newTagCategory, setNewTagCategory] = useState('CUSTOM');

  useEffect(() => {
    fetchAllTags();
  }, [fetchAllTags]);

  // 過濾標籤
  const filteredTags = (tags || []).filter(tag =>
    tag && tag.name && tag.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // 按分類分組標籤
  const groupedTags = filteredTags.reduce((groups: any, tag) => {
    const category = tag.category || 'CUSTOM';
    if (!groups[category]) {
      groups[category] = [];
    }
    groups[category].push(tag);
    return groups;
  }, {});

  // 分類名稱（分類不是狀態，不再依分類上色；選取狀態統一用主色）
  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'INDUSTRY': return t('common.categories.industry');
      case 'SIZE': return t('common.categories.size');
      case 'STATUS': return t('common.categories.status');
      case 'CUSTOM': return t('common.categories.custom');
      default: return category;
    }
  };

  // 切換標籤選中狀態
  const toggleTag = (tagId: string) => {
    onChange(value.includes(tagId) ? value.filter(id => id !== tagId) : [...value, tagId]);
  };

  // 新增標籤
  const handleAddTag = async () => {
    if (!newTagName.trim()) return;

    try {
      const response = await createTag({
        name: newTagName.trim(),
        category: newTagCategory,
        color: CATEGORY_DEFAULT_COLORS[newTagCategory] || '#6B7280'
      });

      const newTag = response.data;

      // 自動選中新創建的標籤
      if (newTag && newTag.id) {
        onChange([...value, newTag.id]);
      }

      // 重置表單
      setNewTagName('');
      setNewTagCategory('CUSTOM');
      setShowAddForm(false);

      // 標籤選擇器、標籤管理頁重新載入
      notifyTagsChanged();
    } catch (error) {
      console.error('Failed to create tag:', error);
    }
  };

  return (
    <div className="space-y-4">
      {/* 搜索框 */}
      <div className="relative">
        <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder={t('common.filters.search')}
          aria-label={t('common.filters.search')}
          className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:border-brand-500"
        />
      </div>

      {/* 標籤雲 */}
      <div className="space-y-4 max-h-48 overflow-y-auto">
        {Object.keys(groupedTags).length === 0 ? (
          <div className="text-center py-6 text-gray-500 dark:text-gray-400">
            {searchTerm ? (
              <div>
                <p className="text-sm">{t('common.noResults')}</p>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm">{t('tags.noTags', { ns: 'pages' })}</p>
                {(!tags || tags.length === 0) && (
                  <div className="text-[13px] bg-slate-50 dark:bg-slate-800/60 border border-line rounded-md p-3">
                    <p className="text-fg mb-1">
                      {t('tags.initialization.hint', { ns: 'pages' })}
                    </p>
                    <p className="text-fg-secondary">
                      {t('tags.initialization.guide', { ns: 'pages' })}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          Object.entries(groupedTags).map(([category, categoryTags]: [string, any[]]) => {
            return (
              <div key={category}>
                <h4 className="text-[13px] font-medium mb-2 text-fg-secondary">
                  {getCategoryLabel(category)} <span className="tabular-nums">({categoryTags.length})</span>
                </h4>
                <div className="flex flex-wrap gap-2">
                  {categoryTags.map((tag) => {
                    const isSelected = value.includes(tag.id);
                    return (
                      <button
                        key={tag.id}
                        type="button"
                        onClick={() => toggleTag(tag.id)}
                        aria-pressed={isSelected}
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[13px] border transition-colors cursor-pointer ${
                          isSelected
                            ? 'border-accent bg-accent-soft text-accent font-medium'
                            : 'border-line bg-transparent text-fg hover:bg-slate-100 dark:hover:bg-slate-800'
                        }`}
                      >
                        {/* 標籤自己的顏色只畫在小圓點（與標籤列表一致） */}
                        <span className="h-2 w-2 shrink-0 rounded-full ring-1 ring-black/10 dark:ring-white/25" style={{ backgroundColor: tag.color }} />
                        {tag.name}
                        {isSelected && <CheckIcon className="h-3.5 w-3.5" aria-hidden="true" />}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* 新增標籤按鈕和表單 */}
      <div className="border-t border-line pt-4">
        {!showAddForm ? (
          <button
            type="button"
            onClick={() => setShowAddForm(true)}
            className="flex items-center space-x-2 text-sm text-brand-600 dark:text-brand-400 hover:text-brand-800 dark:hover:text-brand-300 cursor-pointer"
          >
            <PlusIcon className="h-4 w-4" />
            <span>{t('tags.addNew', { ns: 'pages' })}</span>
          </button>
        ) : (
          <div className="space-y-3 p-3 bg-gray-50 dark:bg-slate-800 rounded-md">
            <div className="flex space-x-2">
              <input
                type="text"
                value={newTagName}
                onChange={(e) => setNewTagName(e.target.value)}
                className="flex-1 px-3 py-1 text-sm border border-gray-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddTag();
                  }
                }}
              />
              <CustomSelect
                value={newTagCategory}
                onChange={(value) => setNewTagCategory(value)}
                options={[
                  { value: 'CUSTOM', label: t('common.categories.custom') },
                  { value: 'INDUSTRY', label: t('common.categories.industry') },
                  { value: 'SIZE', label: t('common.categories.size') },
                  { value: 'STATUS', label: t('common.categories.status') },
                ]}
                className="text-sm"
              />
            </div>
            <div className="flex justify-end space-x-2">
              <button
                type="button"
                onClick={() => {
                  setShowAddForm(false);
                  setNewTagName('');
                  setNewTagCategory('CUSTOM');
                }}
                className="px-3 py-1 text-[13px] text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
              >
                {t('common.actions.cancel')}
              </button>
              <button
                type="button"
                onClick={handleAddTag}
                disabled={!newTagName.trim()}
                className="px-3 py-1 text-[13px] bg-brand-600 text-white rounded hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('common.actions.add')}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* 選中的標籤預覽 */}
      {(() => {
        // 只顯示實際存在的標籤
        const validTags = value.filter(tagId => tags?.find(t => t.id === tagId));
        return validTags.length > 0 && (
          <div className="border-t border-line pt-4">
            <h5 className="text-[13px] font-medium text-fg-secondary mb-2">
              {t('common.forms.labels.selectedTags')} ({validTags.length})
            </h5>
          <div className="flex flex-wrap gap-1">
            {value.map((tagId) => {
              const tag = tags?.find(t => t.id === tagId);
              if (!tag) return null;
              return (
                <span
                  key={tagId}
                  className="inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-0.5 rounded-full text-[13px] border border-line text-fg"
                >
                  <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: tag.color }} />
                  {tag.name}
                  <button
                    type="button"
                    onClick={() => toggleTag(tagId)}
                    aria-label={t('common.actions.remove')}
                    className="px-0.5 text-fg-secondary hover:text-fg transition-colors cursor-pointer"
                  >
                    ×
                  </button>
                </span>
              );
            })}
          </div>
        </div>
        );
      })()}

    </div>
  );
};

export default TagCloudSelector;